import {
    HttpErrorResponse,
    HttpEvent,
    HttpHandlerFn,
    HttpRequest,
    HttpStatusCode
} from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '../services/auth.service';
import { catchError, Observable, Subject, switchMap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';

const SKIP_URLS = [
    'autenticacion/refrescartoken',
    'autenticacion/AutenticarNuevoProducto'
];

let isRefreshing = false;
const refreshDone$ = new Subject<void>();

function isBffRequest(url: string): boolean {
    return url.startsWith(environment.urlApiMenuBff);
}

function shouldSkipInterceptor(url: string): boolean {
    return SKIP_URLS.some(skip => url.includes(skip));
}

function cloneWithAuth(req: HttpRequest<any>, authService: AuthService): HttpRequest<any> {
    const accessToken = authService.currentAccessToken?.trim() ?? '';
    const permission = authService.currentPermission?.trim() ?? '';

    if (!accessToken) {
        return req;
    }

    const headers: Record<string, string> = {
        'Authorization': `Bearer ${accessToken}`
    };

    if (permission) {
        headers['X-Permission'] = permission;
    }

    return req.clone({ setHeaders: headers });
}

function handleTokenRefresh(
    req: HttpRequest<any>,
    next: HttpHandlerFn,
    authService: AuthService
): Observable<HttpEvent<any>> {
    if (isRefreshing) {
        return refreshDone$.pipe(
            switchMap(() => {
                const retryReq = cloneWithAuth(req, authService);
                return next(retryReq);
            })
        );
    }

    isRefreshing = true;

    const ctx = authService.getContextoPermisos();

    return authService.refreshToken().pipe(
        switchMap(() => {
            return authService.obtenerTokenPermisos(ctx.idAplicacion, ctx.filtroAdicional);
        }),
        switchMap((data) => {
            authService.aplicarRespuestaAutenticarNuevoProducto(data);

            isRefreshing = false;
            refreshDone$.next();

            const retryReq = cloneWithAuth(req, authService);
            return next(retryReq);
        }),
        catchError((refreshError) => {
            isRefreshing = false;
            refreshDone$.next();
            return throwError(() => refreshError);
        })
    );
}

function handleBffError(
    err: HttpErrorResponse,
    req: HttpRequest<any>,
    next: HttpHandlerFn,
    authService: AuthService
): Observable<HttpEvent<any>> {
    if (err.status === HttpStatusCode.Unauthorized) {
        return handleTokenRefresh(req, next, authService);
    }
    if (err.status === HttpStatusCode.Forbidden) {
        return authService.forzarRenovacionTokenPermisos$().pipe(
            switchMap(() => {
                const retryReq = cloneWithAuth(req, authService);
                return next(retryReq).pipe(
                    catchError((err2: unknown) => {
                        const httpErr = err2 as HttpErrorResponse;
                        if (httpErr.status === HttpStatusCode.Unauthorized) {
                            return handleTokenRefresh(req, next, authService);
                        }
                        return throwError(() => err2);
                    })
                );
            }),
            catchError((e) => throwError(() => e))
        );
    }
    return throwError(() => err);
}

function sendWithAuthHandling(
    req: HttpRequest<any>,
    next: HttpHandlerFn,
    authService: AuthService
): Observable<HttpEvent<any>> {
    const authReq = cloneWithAuth(req, authService);
    return next(authReq).pipe(
        catchError((err: unknown) => {
            const httpErr = err as HttpErrorResponse;
            if (isBffRequest(req.url)) {
                return handleBffError(httpErr, req, next, authService);
            }
            if (httpErr.status === HttpStatusCode.Unauthorized) {
                return handleTokenRefresh(req, next, authService);
            }
            return throwError(() => err);
        })
    );
}

export function JwtInterceptor(req: HttpRequest<any>, next: HttpHandlerFn): Observable<HttpEvent<any>> {
    const authService = inject(AuthService);

    if (shouldSkipInterceptor(req.url)) {
        return next(req);
    }

    if (isBffRequest(req.url)) {
        const permission = authService.currentPermission?.trim() ?? '';
        const accessToken = authService.currentAccessToken?.trim() ?? '';
        if (!permission || !accessToken) {
            const ctx = authService.getContextoPermisos();
            return authService.ensureTokenPermisos$(ctx.idAplicacion, ctx.filtroAdicional).pipe(
                switchMap(() => sendWithAuthHandling(req, next, authService)),
                catchError((e) => throwError(() => e))
            );
        }
    }

    return sendWithAuthHandling(req, next, authService);
}
